import * as THREE from "three";

export const WATCH = { minDistance: 0.2, maxDistance: 0.75, maxDrop: 0.45, closeDrop: 0.5, enterAngle: 45, dwellMs: 250, closeMs: 350 };
export const MAX_TELEPORT_DISTANCE = 14;
export const SOURCE_MAX_AGE_MS = 120;
export const HAND_MENU_WALK_AWAY_DISTANCE = 1.5;

export const inputKind = (source) => source?.hand ? "hand" : source?.gripSpace ? "controller" : "natural";
export const hasHandInput = (sources) => Array.from(sources || []).some(source => !!source.hand);
export const freshWatch = () => ({ open: false, since: null, awaySince: null, suppressed: false });

const finitePosition = position => !!position && [position.x, position.y, position.z].every(Number.isFinite);

export function isHandRaised({ wrist, head } = {}) {
  return finitePosition(wrist) && finitePosition(head) && wrist.y - head.y >= -WATCH.closeDrop;
}

export function isRaisedMenuPose({ wrist, head, forward } = {}) {
  if (![wrist, head, forward].every(finitePosition)) return false;
  const offset = new THREE.Vector3(wrist.x - head.x, wrist.y - head.y, wrist.z - head.z);
  const distance = offset.length();
  return distance >= WATCH.minDistance && distance <= WATCH.maxDistance && offset.y >= -WATCH.maxDrop &&
    offset.dot(forward) > 0;
}

export function wristMenuWalkedAway(head, panelPosition) {
  if (![head, panelPosition].every(finitePosition)) return false;
  // Floor height changes and crouching should not dismiss a fixed world panel.
  return Math.hypot(head.x - panelPosition.x, head.z - panelPosition.z) >= HAND_MENU_WALK_AWAY_DISTANCE;
}

export function isWatchPose({ wrist, index, pinky, dorsal, head, forward }) {
  if (![wrist, index, pinky, dorsal, head, forward].every(Boolean)) return false;
  const offset = wrist.clone().sub(head);
  const distance = offset.length();
  const palmArea = index.clone().sub(wrist).cross(pinky.clone().sub(wrist)).length();
  return palmArea > 0.00005 && distance >= WATCH.minDistance && distance <= WATCH.maxDistance &&
    offset.y >= -WATCH.maxDrop && offset.dot(forward) > 0 &&
    dorsal.clone().normalize().dot(offset.normalize().negate()) >= Math.cos(THREE.MathUtils.degToRad(WATCH.enterAngle));
}

export function stepWatch(previous, { tracked, pose, raised = pose, walkedAway = false, now }) {
  const state = { ...previous };
  // Once opened, the left hand is only a launcher. The panel stays available to
  // the right hand even when the left hand is lowered or no longer tracked.
  if (state.open) return walkedAway ? { ...freshWatch(), suppressed: true } : state;
  if (!tracked) return { ...state, since: null, awaySince: null };
  if (state.suppressed) return raised ? state : freshWatch();
  if (pose && Number.isFinite(now)) {
    state.awaySince = null;
    if (state.since === null) state.since = now;
    if (now - state.since >= WATCH.dwellMs) state.open = true;
  } else {
    state.since = null;
    state.awaySince = null;
  }
  return state;
}

export const freshPoke = () => ({ armed: false, fired: false, travel: 0 });
// depth and advance are meters. advance measures FINGER motion toward the panel,
// not relative motion caused by moving the left wrist into a stationary finger.
export function stepPoke(previous, { inside, depth, advance }) {
  if (!inside || !Number.isFinite(depth) || depth < -0.02 || depth > 0.06) return { state: freshPoke(), activate: false };
  const state = { ...previous };
  if (depth >= 0.03) { state.fired = false; state.armed = true; state.travel = 0; }
  else if (!state.fired && depth >= 0.02) state.armed = true;
  if (state.armed) state.travel = Math.max(0, state.travel + advance);
  const activate = state.armed && !state.fired && depth <= 0.005 && state.travel >= 0.006 && advance > 0.0005;
  if (activate) { state.fired = true; state.armed = false; }
  return { state, activate };
}

export const freshSelection = () => ({ source: null, target: null, consumed: false });
export function startSelection(source, target, direct) { return { source, target, consumed: direct }; }
export function consumeSelection(state) { return { ...state, consumed: true }; }
export function canCommitSelection(state, source, target, direct) {
  return !!target && state.source === source && state.target === target && !state.consumed && !direct;
}

export function isEffectivelyVisible(object) {
  for (let current = object; current; current = current.parent) if (!current.visible) return false;
  return !!object;
}

export const initialSettings = () => ({ teleportEnabled: true, carOnly: false, powertrain: false });
export function setCarOnly(settings, value) {
  return { ...settings, carOnly: value, powertrain: value ? settings.powertrain : false, teleportEnabled: value ? false : settings.teleportEnabled };
}
export function setPowertrain(settings, value) {
  return { ...settings, powertrain: value, carOnly: value ? true : settings.carOnly, teleportEnabled: value ? false : settings.teleportEnabled };
}
export function renderingPolicy(carOnly, blendMode) {
  const seeThrough = blendMode === "additive" || blendMode === "alpha-blend";
  return { showEnvironment: !carOnly, background: carOnly && seeThrough ? null : "#000000", clearAlpha: carOnly && seeThrough ? 0 : 1, seeThrough };
}
export function validTeleport(point, head, { enabled, menuOpen, focused, ageMs = 0 }) {
  return !!point && !!head && enabled && !menuOpen && focused && ageMs >= 0 && ageMs <= SOURCE_MAX_AGE_MS && point.distanceTo(head) <= MAX_TELEPORT_DISTANCE;
}
export function teleportOffset(point, head) { return new THREE.Vector3(point.x - head.x, 0, point.z - head.z); }

export function chooseSource(sources, handedness) {
  // A tracked physical controller wins only for its own side; mixed input works.
  const candidates = sources.filter((source) => source.inputSource.handedness === handedness && source.tracked);
  return candidates.find((source) => source.kind === "controller") ?? candidates.find((source) => source.kind === "hand") ?? candidates[0] ?? null;
}
